import {
  DataVersion,
  IncidentProduct,
  LocationAlarmUpdateType,
  ScanAlarmUpdateType,
} from '@hakimo-ui/hakimo/types';
import { appendVersionParam } from '@hakimo-ui/hakimo/util';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);

export type EditableField =
  | 'reported_description'
  | 'reported_resolution_comment'
  | 'reported_cameras';

export const EDITABLE_UPDATE_TYPES = new Set<string>([
  LocationAlarmUpdateType.DISPATCH,
  LocationAlarmUpdateType.ARRIVAL,
  LocationAlarmUpdateType.ESCALATION_SNAPSHOT_COMMENT,
  ScanAlarmUpdateType.DISPATCH,
  ScanAlarmUpdateType.ARRIVAL,
  ScanAlarmUpdateType.COMMENT_FOR_SNAPSHOT,
]);

export function formatInZone(
  isoUtc: string | null | undefined,
  zone: string | null | undefined,
  template = 'YYYY-MM-DD HH:mm:ss'
): string {
  if (!isoUtc) {
    return '-';
  }
  const parsed = zone ? dayjs.utc(isoUtc).tz(zone) : dayjs.utc(isoUtc);
  return parsed.isValid() ? parsed.format(template) : '-';
}

/**
 * Parses the pre-formatted zoned stamp v3's `get_neighbour_la` sends instead of
 * alarm_start_time_utc + timezone --
 * `"YYYY-MM-DD HH:mm:ss <IANA zone>"`, e.g. `"2026-09-11 06:04:11 US/Pacific"`.
 * IANA zone ids never contain a space, so the last space is the separator.
 *
 * Neighbour rows only: the incident *detail* endpoint returns the v2 pair on
 * both versions.
 */
function parseZonedStamp(value: string | null | undefined) {
  if (!value) {
    return null;
  }
  const splitAt = value.lastIndexOf(' ');
  if (splitAt !== -1) {
    try {
      const zoned = dayjs.tz(value.slice(0, splitAt), value.slice(splitAt + 1));
      if (zoned.isValid()) {
        return zoned;
      }
    } catch {
      // Trailing token wasn't a zone id; dayjs.tz throws rather than
      // returning an invalid instance. Fall through to a plain parse.
    }
  }
  const plain = dayjs(value);
  return plain.isValid() ? plain : null;
}

interface AlarmStartTime {
  /** v2 shape: instant + zone, formatted client-side. */
  alarm_start_time_utc?: string | null;
  timezone?: string | null;
  /**
   * v3 neighbour-row shape: a single pre-formatted zoned stamp, sent without
   * alarm_start_time_utc/timezone. Prefer the v2 pair when present and fall
   * back to parsing this, so the same helpers serve both shapes.
   */
  alarm_start_time?: string | null;
}

/** Display form of an alarm's start time, whichever shape the API sent. */
export function formatAlarmStartTime(
  alarm: AlarmStartTime,
  template = 'MMM D, YYYY h:mm:ss A'
): string {
  if (alarm.alarm_start_time_utc) {
    return formatInZone(alarm.alarm_start_time_utc, alarm.timezone, template);
  }
  const parsed = parseZonedStamp(alarm.alarm_start_time);
  return parsed ? parsed.format(template) : '-';
}

/** Sortable epoch ms for an alarm's start time; 0 when absent. */
export function alarmStartTimeValue(alarm: AlarmStartTime): number {
  if (alarm.alarm_start_time_utc) {
    return new Date(alarm.alarm_start_time_utc).getTime();
  }
  return parseZonedStamp(alarm.alarm_start_time)?.valueOf() ?? 0;
}

export function formatDate(
  isoUtc: string | null | undefined,
  zone: string | null | undefined
): string {
  return formatInZone(isoUtc, zone, 'YYYY-MM-DD');
}

export function formatTime(
  isoUtc: string | null | undefined,
  zone: string | null | undefined
): string {
  return formatInZone(isoUtc, zone, 'HH:mm:ss');
}

export function alarmDetailPath(
  product: IncidentProduct,
  alarmId: number | string,
  dataVersion?: DataVersion
): string {
  const path =
    product === 'scan'
      ? `/alarm-groups/${alarmId}`
      : `/location-alarms/${alarmId}`;
  // Scan is pinned to v2, so its callers pass no version and this is a no-op.
  return appendVersionParam(path, dataVersion);
}

export function shortAlarmId(id: number | string): string {
  const value = String(id);
  return value.length > 8 ? `${value.slice(0, 8)}…` : value;
}
